import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
// component
import { Alert } from 'components/common/Alert';
import NoDataFound from 'components/common/NoDataFound';
// constants, context, interfaces, graphql, styles, helper
import { flexEnd } from 'theme/styleConstant';
import { FactSheetModalProps } from 'interfaces';
import { formatAgeGroups, formatCalcFormulaIngredients } from 'lib/helper';
import { CLOSE_TEXT, FACT_SHEET_TEXT, HTTP_STATUS, SOMETHING_WENT_WRONG_TEXT } from 'constants/index';
import { FactBoxType, FormulaBlendInput, useGetFactSheetLazyQuery } from 'generated/graphql';

const FactSheetModal: FC<FactSheetModalProps> = ({
  open,
  onClose,
  factBoxType,
  capsuleTypeId,
  productTypeId,
  excipientTypeId,
  ingredientsBlend,
  sweetenerSystemId,
  formulaIngredients,
  innerCapsuleTypeId,
  outerCapsuleTypeId,
  productFormulationTypeId,
  sandCoatingId,
  productSizeId,
  subProductTypeId,
  tabletCoating,
  ageGroups,
}) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [fetchFactSheet, { loading }] = useGetFactSheetLazyQuery({
    onCompleted: (res) => {
      const { getFactSheet } = res || {};
      const { pdfStr, response } = getFactSheet || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setPdfUrl(pdfStr || '');
      } else {
        setPdfUrl('');
      }
    },
    onError: (error) => {
      setPdfUrl('');
      Alert.error(error?.message || SOMETHING_WENT_WRONG_TEXT);
    },
  });

  const fetchFormulaFactSheet = useCallback(async () => {
    const ingredients = formatCalcFormulaIngredients(formulaIngredients ?? []);

    const ingredientBlends = ingredientsBlend?.map<FormulaBlendInput>(
      ({ ingredients: blendIngredient, name }) => {
        const blendIngredients = formatCalcFormulaIngredients(blendIngredient);
        return {
          name,
          formulaIngredients: blendIngredients,
        };
      },
    );

    const formattedAgeGroups = formatAgeGroups(ageGroups ?? []);

    await fetchFactSheet({
      variables: {
        getFactSheetInput: {
          factBoxType: factBoxType ? (factBoxType as FactBoxType) : FactBoxType.Supplement,

          productTypeId,
          productFormulationTypeId,
          ...(subProductTypeId && { subProductTypeId }),

          formulaIngredients: ingredients,
          formulaBlends: ingredientBlends,

          ...(excipientTypeId && { excipientTypeId }),

          ...(capsuleTypeId && { capsuleTypeId }),
          ...(innerCapsuleTypeId && { innerCapsuleTypeId }),
          ...(outerCapsuleTypeId && { outerCapsuleTypeId }),

          ...(sweetenerSystemId && { sweetenerSystemId }),

          ...(sandCoatingId && { sandCoatingId }),
          ...(tabletCoating && { tabletCoatingId: tabletCoating }),

          ...(productSizeId && { productSizeId }),

          ageGroups: formattedAgeGroups,
        },
      },
    });
  }, [
    ingredientsBlend,
    formulaIngredients,

    fetchFactSheet,

    productSizeId,
    productTypeId,
    subProductTypeId,
    productFormulationTypeId,

    factBoxType,

    excipientTypeId,
    sweetenerSystemId,

    capsuleTypeId,
    innerCapsuleTypeId,
    outerCapsuleTypeId,

    sandCoatingId,
    tabletCoating,

    ageGroups,
  ]);

  useEffect(() => {
    open && fetchFormulaFactSheet();
  }, [fetchFormulaFactSheet, open]);

  return (
    <Dialog open={open} fullWidth maxWidth={'lg'}>
      <DialogTitle>{FACT_SHEET_TEXT}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box textAlign="center">
            <CircularProgress size={40} />
          </Box>
        ) : pdfUrl ? (
          <object>
            <embed type="text/html" width="100%" height="600" src={pdfUrl} />
          </object>
        ) : (
          <NoDataFound noData={!pdfUrl} />
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={flexEnd}>
          <Button variant="outlined" color="primary" onClick={onClose} size="small">
            {CLOSE_TEXT}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FactSheetModal;
